import React, { useEffect, useState } from 'react'
import EChartComponent from '@/components/EChartComponent'
import { Col, Row, Segmented, DatePicker } from 'antd'
import axios from 'axios'
import dayjs, { Dayjs } from 'dayjs'
import { StationVirtualPoint } from '@/type/stationType'
import { calcVirtualByInterval, calcVirtualCumulantData } from '@/utils/getData'
import getTimeStamp from '@/utils/getTimeStamp'
import appConfig from '@/config'
import useGlobalConfigStore from '@/stores/globalConfig'
import { useTranslation } from 'react-i18next'

const Chart: React.FC<{
  deviceId: number
  points?: StationVirtualPoint[]
  propertyCodes?: { label: string; code: string; unit: string }[]
}> = React.memo(({ deviceId, points, propertyCodes }) => {
  const { t } = useTranslation()
  const [timeInterval, setTimeInterval] = useState('5min')
  const [timeType, setTimeType] = useState('day')
  const [chooseTime, setChooseTime] = useState<Dayjs>(dayjs())
  const [echartsOption, setEchartOption] = useState<echarts.EChartsOption>({})
  const setLoading = useGlobalConfigStore(state => state.setLoading)

  useEffect(() => {
    if (deviceId !== 0 && ((points && points.length > 0) || (propertyCodes && propertyCodes.length > 0))) {
      if (timeType === 'day') {
        getDayData()
      } else {
        getColumnData()
      }
    }
  }, [deviceId, points, propertyCodes, timeInterval, timeType, chooseTime])

  const getDayData = async () => {
    setLoading(true)
    let res: any
    const series: any[] = [],
      yAxis: any[] = [],
      legendData: string[] = [],
      grid = { left: 10, right: 10, top: 30, bottom: 60 }
    const { startTime, endTime } = getTimeStamp('day', chooseTime.toDate())
    for (let point of points as StationVirtualPoint[]) {
      res = await calcVirtualByInterval(point, startTime, endTime, timeInterval)
      // console.log(res)
      if (!yAxis.find(item => item.name === res.unit)) {
        if (yAxis.length % 2 === 0) {
          grid.left += 50
        } else {
          grid.right += 50
        }
        yAxis.push({
          type: 'value',
          name: res.unit,
          position: yAxis.length % 2 === 0 ? 'left' : 'right',
          splitLine: { show: true },
          offset: yAxis.length % 2 === 0 ? 50 * (yAxis.length / 2) : 50 * ((yAxis.length - 1) / 2),
        })
      }
      series.push({
        name: point.name,
        type: 'line',
        lineStyle: { width: 2 },
        smooth: true,
        symbol: 'none',
        data: res.data.map((item: { value: number; time: number }) => [item.time, item.value]),
        yAxisIndex: yAxis.findIndex(item => item.name === res.unit),
      })
      legendData.push(point.name)
    }
    if (propertyCodes && propertyCodes.length > 0) {
      res = await axios.post(
        `${appConfig.hBaseUrl}demo/getAggregateData?prefix=${appConfig.prefix}&start=${startTime}&end=${endTime}&tg=${timeInterval}`,
        propertyCodes.map(item => ({ deviceId: deviceId, propertyCode: item.code, fn: 'sampling' }))
      )
      if (res.status === 200) {
        legendData.push(...propertyCodes.map(item => item.label))
        propertyCodes.forEach(point => {
          const target = res.data.data.find((item: any) => item.propertyCode === point.code)
          if (target) {
            if (!yAxis.find(item => item.name === point.unit)) {
              if (yAxis.length % 2 === 0) {
                grid.left += 50
              } else {
                grid.right += 50
              }
              yAxis.push({
                type: 'value',
                name: point.unit,
                position: yAxis.length % 2 === 0 ? 'left' : 'right',
                splitLine: { show: true },
                offset: yAxis.length % 2 === 0 ? 50 * (yAxis.length / 2) : 50 * ((yAxis.length - 1) / 2),
              })
            }
            series.push({
              name: point.label,
              type: 'line',
              lineStyle: { width: 2 },
              smooth: true,
              symbol: 'none',
              data: target.data.map((item: { t1: number; t2: number; value: number }) => [item.t2, item.value]),
              yAxisIndex: yAxis.findIndex(item => item.name === point.unit),
            })
          }
        })
      }
    }
    const optionTemp: echarts.EChartsOption = {
      tooltip: {
        show: true,
        trigger: 'axis',
        formatter(params: any[]) {
          const time = new Date(params[0].axisValue)
          let showTime = `${time.getHours().toString().padStart(2, '0')}:${time
            .getMinutes()
            .toString()
            .padStart(2, '0')}:${time.getSeconds().toString().padStart(2, '0')}`
          let showText = `${showTime}`
          params.forEach(param => {
            showText += `<div style="display:flex;justify-content:space-between;align-items:center;"><div>${
              param.marker
            }${param.seriesName}</div><b>&nbsp;&nbsp;&nbsp;&nbsp;${param.value[1].toFixed(2)}</b></div>`
          })
          return showText
        },
      } as any,
      legend: {
        data: legendData,
        type: 'scroll',
        pageIconColor: '#000',
        pageIconInactiveColor: '#999',
        pageIconSize: 10,
        pageTextStyle: {
          fontSize: 12,
          color: '#000',
        },
        bottom: 0,
        left: 'center',
        textStyle: {
          fontSize: 12,
          color: '#000',
        },
        inactiveColor: '#999999',
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          fontSize: 12,
        },
      },
      grid,
      yAxis,
      series,
    }
    setEchartOption(optionTemp)
    setLoading(false)
  }

  const getColumnData = async () => {
    setLoading(true)
    let res: any
    const series: any[] = [],
      yAxis: any[] = [],
      legendData: string[] = [],
      grid = { left: 10, right: 10, top: 30, bottom: 60 }
    // const { startTime, endTime } = getTimeStamp(timeType, chooseTime.toDate())
    for (let point of points as StationVirtualPoint[]) {
      res = await calcVirtualCumulantData([point], timeType, chooseTime.toDate())
      console.log(res)
      if (!yAxis.find(item => item.name === res.unit)) {
        if (yAxis.length % 2 === 0) {
          grid.left += 30
        } else {
          grid.right += 30
        }
        yAxis.push({
          type: 'value',
          name: res.unit,
          position: yAxis.length % 2 === 0 ? 'left' : 'right',
          splitLine: { show: true },
          offset: yAxis.length % 2 === 0 ? 30 * (yAxis.length / 2) : 30 * ((yAxis.length - 1) / 2),
        })
      }
      series.push({
        name: point.name,
        type: 'bar',
        lineStyle: { width: 2 },
        smooth: true,
        symbol: 'none',
        data: res.data.map((item: string, index: number) => [res.label[index], item]),
        yAxisIndex: yAxis.findIndex(item => item.name === res.unit),
      })
      legendData.push(point.name)
    }
    if (propertyCodes && propertyCodes.length > 0) {
      let formData = new FormData()
      formData.append(
        'data',
        JSON.stringify([
          {
            deviceId,
            defines: propertyCodes.map(item => item.code),
            prefix: appConfig.prefix,
          },
        ])
      )
      formData.append('time', chooseTime.toDate().getTime().toString())
      formData.append('type', timeType)
      formData.append('token', 'hadkjlvnouq8293015hf0d9sh')
      formData.append('zoneStr', Intl.DateTimeFormat().resolvedOptions().timeZone)
      res = await axios.post(`${appConfig.hBaseUrl}demo/getGroupAccumulativeDataByZone`, formData)
      console.log(res)
    }
    const optionTemp: echarts.EChartsOption = {
      tooltip: {
        show: true,
        trigger: 'axis',
        formatter(params: any[]) {
          const time = new Date(params[0].axisValue)
          let showTime = ''
          if (timeType === 'week' || timeType === 'month') {
            showTime = `${time.getDate().toString().padStart(2, '0')}/${(time.getMonth() + 1)
              .toString()
              .padStart(2, '0')}/${time.getFullYear()}`
          } else if (timeType === 'year') {
            showTime = `${(time.getMonth() + 1).toString().padStart(2, '0')}/${time.getFullYear()}`
          } else if (timeType === 'overyear') {
            showTime = `${time.getFullYear()}`
          }
          let showText = `${showTime}`
          params.forEach(param => {
            showText += `<div style="display:flex;justify-content:space-between;align-items:center;"><div>${param.marker}${param.seriesName}</div><b>&nbsp;&nbsp;&nbsp;&nbsp;${param.value[1]}</b></div>`
          })
          return showText
        },
      } as any,
      legend: {
        data: legendData,
        bottom: 0,
        left: 'center',
        textStyle: {
          fontSize: 12,
          color: '#fff',
        },
        inactiveColor: '#999999',
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          fontSize: 12,
        },
      },
      grid,
      yAxis,
      series,
    }
    setEchartOption(optionTemp)
    setLoading(false)
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <Row style={{ height: 'calc(100% - 400px)' }}>
        <Col span={8}>
          <span className='Solar-three-title'>{t('menu.chart')}</span>
        </Col>
        <Col span={16}>
          <div className='filter'>
            {timeType === 'day' && (
              <Segmented
                value={timeInterval}
                style={{ marginBottom: 8 }}
                options={['5min', '15min', '30min', '60min']}
                onChange={setTimeInterval}
              />
            )}
            {timeType !== 'overyear' && (
              <DatePicker
                suffixIcon={<img src='/assets/image/date.svg' />}
                value={chooseTime}
                onChange={setChooseTime}
                format={
                  timeType === 'day'
                    ? 'DD-MM-YYYY'
                    : timeType === 'week'
                    ? 'YYYY-wo'
                    : timeType === 'month'
                    ? 'MM-YYYY'
                    : 'YYYY'
                }
                picker={
                  timeType === 'day' ? 'date' : timeType === 'week' ? 'week' : timeType === 'month' ? 'month' : 'year'
                }
              />
            )}
            {/* <Segmented
              value={timeType}
              style={{ marginBottom: 8 }}
              options={[
                { label: "Daily", value: "day" },
                { label: "Weekly", value: "week" },
                { label: "Monthly", value: "month" },
                { label: "Year", value: "year" },
                { label: "Overyear", value: "overyear" },
              ]}
              onChange={setTimeType}
            /> */}
          </div>
        </Col>
      </Row>
      <div style={{ width: '100%', height: '400px' }}>
        <EChartComponent staticOption={echartsOption} />
      </div>
    </div>
  )
})

export default Chart
